import React from 'react';
import { Link } from 'react-router-dom';
import AppStoreButton from '../components/AppStoreButton';
import DiscordButton from '../components/DiscordButton';
import './About.css';

const About = () => {
  return (
    <div className="page-container about">
      <header className="about-header">
        <h1>About Hanzi Flashcards</h1>
        <h2>Master Chinese Characters Your Way</h2>
        <p className="intro">
          Hanzi Flashcards is an innovative web application designed to help you learn Chinese characters effectively through customizable triple-sided flashcards. Whether you're studying Simplified or Traditional Chinese, our unique approach lets you tailor your learning experience to focus on character recognition, pronunciation, or translation skills.
        </p>
      </header>

      <section className="why-choose">
        <h2>Why Choose Hanzi Flashcards?</h2>

        <div className="feature-block">
          <h3>Instant Flashcard Creation</h3>
          <p>Turn any Chinese text into flashcards instantly:</p>
          <ul>
            <li>Just paste your text and get complete flashcards with characters, pinyin, and translations</li>
            <li>Create up to 20 cards at once (free) or 50 cards (premium)</li>
            <li>Perfect for learning from articles, chat messages, or any text you encounter</li>
          </ul>
        </div>

        <div className="feature-block">
          <h3>Triple-Sided Learning</h3>
          <p>Our flashcards feature three essential elements of Chinese character learning:</p>
          <ul>
            <li>Hanzi characters (both Simplified and Traditional Chinese)</li>
            <li>Pinyin pronunciation</li>
            <li>Translation</li>
          </ul>
        </div>

        <div className="feature-block">
          <h3>Flexible Learning Approach</h3>
          <p>Unlike traditional flashcard apps, Hanzi Flashcards lets you choose which side appears first:</p>
          <ul>
            <li>Start with characters to enhance recognition skills</li>
            <li>Begin with translations to improve Chinese production</li>
            <li>Focus on pinyin to strengthen pronunciation and listening abilities</li>
          </ul>
        </div>

        <div className="feature-block">
          <h3>Personalized Learning Sets</h3>
          <ul>
            <li>Create custom sets of flashcards</li>
            <li>Combine multiple sets for comprehensive practice</li>
            <li>Track your progress and review mistakes to improve faster</li>
          </ul>
        </div>
      </section>

      <section className="features">
        <h2>Features</h2>

        <div className="feature-block">
          <h3>Core Functionality</h3>
          <ul>
            <li>Triple-sided flashcards (Character, Pinyin, Translation)</li>
            <li>Customizable display order</li>
            <li>Support for both Simplified and Traditional Chinese</li>
            <li>User-defined flashcard sets</li>
            <li>Wrong-answer review mode</li>
          </ul>
        </div>

        <div className="feature-block">
          <h3>Smart Card Creation</h3>
          <ul>
            <li>Instant flashcard generation from any Chinese text</li>
            <li>Create up to 20 cards at once (free tier) or 50 cards (premium)</li>
            <li>7-day free trial of premium card creation for new users</li>
            <li>Premium subscribers get unlimited access to batch card creation</li>
          </ul>
        </div>

        <div className="feature-block">
          <h3>Progressive Web App</h3>
          <ul>
            <li>Install on any device for quick access</li>
            <li>Works across all modern browsers</li>
            <li>Responsive design for desktop and mobile use</li>
          </ul>
        </div>
      </section>

      <section className="tutorial-section">
        <h2>See How It Works</h2>
        <div className="tutorial-container">
          <div className="tutorial-video">
            <video 
              controls
              playsInline
              preload="metadata"
              poster="/images/tutorial-poster.jpg"
              className="feature-video"
            >
              <source src="/hanzi_manual.mov" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      <section className="pricing">
        <h2>Subscription Plans</h2>
        <div className="plans">
          <div className="plan-block">
            <h3>Free Tier</h3>
            <div className="price">$0<span>/forever</span></div>
            <ul>
              <li>Up to 5 sets</li>
              <li>Up to 20 flashcards per set</li>
              <li>Create 20 cards at once from text</li>
              <li>7-day trial of premium features</li>
              <li>All core features included</li>
            </ul>
            <Link to="/" className="cta-button">Start Free</Link>
          </div>

          <div className="plan-block featured">
            <h3>Monthly Plan</h3>
            <div className="price">$2.99<span>/month</span></div>
            <ul>
              <li>Unlimited flashcards</li>
              <li>Unlimited sets</li>
              <li>Create 50 cards at once from text</li>
              <li>All premium features</li>
              <li>Priority support</li>
            </ul>
            <Link to="/" className="cta-button">Subscribe Now</Link>
          </div>

          <div className="plan-block">
            <h3>Annual Plan</h3>
            <div className="price">$29.99<span>/year</span></div>
            <ul>
              <li>Unlimited flashcards</li>
              <li>Unlimited sets</li>
              <li>Create 50 cards at once from text</li>
              <li>All premium features</li>
              <li>Priority support</li>
              <li>Save 16% compared to monthly</li>
            </ul>
            <Link to="/" className="cta-button">Subscribe Now</Link>
          </div>
        </div>
      </section>

      <section className="future">
        <h2>Future Development</h2>
        <p>We're committed to continuous improvement and will be adding new features based on user feedback and needs. Your suggestions are welcome as we grow and enhance the application.</p>
      </section>

      <section className="get-started">
        <h2>Get Started</h2>
        <p>Start your Chinese learning journey today with Hanzi Flashcards. Try our free tier to experience the unique benefits of triple-sided flashcards and discover why they're more effective for learning Chinese characters.</p>
        <Link to="/" className="cta-button">Try It Now</Link>
      </section>

      <section className="download-section">
        <div className="download-options">
          <div className="download-option">
            <p className="download-text">Also available as a native app on the App Store</p>
            <AppStoreButton />
          </div>
          <div className="download-option">
            <p className="download-text">Join our community on Discord</p>
            <DiscordButton />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;